<template>
  <div>
    <b-card no-body class="mb-0">
      <b-row class="m-1">
        <b-col cols="6" class="d-flex align-items-center">
          <v-select
            v-model="perPage"
            :clearable="false"
            class="per-page-selector d-inline-block w-auto"
            :options="[5, 10, 20, 50]"
            @input="onPerPageChange"
          />
        </b-col>
        <b-col cols="6" class="d-flex justify-content-end">
          <b-button variant="primary" @click="$router.push('/cms/addcms')">Add CMS</b-button>
        </b-col>
      </b-row>

      <div class="m-2">
        <b-table
          :items="items"
          :fields="fields"
          :busy="isLoading"
          primary-key="id"
          responsive
          show-empty
          empty-text="No CMS pages found"
        >
          <template #table-busy>
            <div class="text-center text-secondary my-2">
              <b-spinner class="align-middle" />
            </div>
          </template>

          <template #cell(srNo)="row">
            {{ (currentPage - 1) * perPage + row.index + 1 }}
          </template>

          <template #cell(link)="row">
            <span class="cms-page-link">{{ formatPageLink(row.item) }}</span>
          </template>

          <template #cell(status)="row">
            <b-badge :variant="statusVariant(row.item)">
              {{ statusLabel(row.item) }}
            </b-badge>
          </template>

          <template #cell(actions)="row">
            <row-actions
              @edit="$router.push({ name: 'edit-cms', params: { id: row.item.id } })"
              @delete="removeRow(row.item.id)"
            >
              <template #extra>
                <button type="button" class="row-action row-action--success" title="Approve" @click="approvePage(row.item)">
                  <feather-icon icon="CheckCircleIcon" size="16" />
                </button>
                <button type="button" class="row-action row-action--danger" title="Reject" @click="openRejectModal(row.item)">
                  <feather-icon icon="XCircleIcon" size="16" />
                </button>
              </template>
            </row-actions>
          </template>
        </b-table>

        <AdminPaginationBar
          :page="currentPage"
          :per-page="perPage"
          :total="totalRows"
          @change="onPageChange"
        />
      </div>
    </b-card>

    <b-modal v-model="rejectModalOpen" title="Reject CMS Page" hide-footer>
      <b-form-group label="Reason" label-for="reject-reason">
        <b-form-textarea
          id="reject-reason"
          v-model="rejectReason"
          rows="4"
          placeholder="Enter rejection reason"
        />
      </b-form-group>
      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-1" @click="closeRejectModal">Cancel</b-button>
        <b-button variant="danger" @click="rejectPage">Reject</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormTextarea,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import RowActions from '@/components/admin/RowActions.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import { notifyError, notifySuccess } from '@/utils/uiActions'
import { applyListPagination, unwrapAdminResponse, unwrapPaginatedList } from '@/utils/adminApiHelpers'

export default {
  name: 'CmsEditor',
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormTextarea,
    BRow,
    BSpinner,
    BTable,
    vSelect,
    AdminPaginationBar,
    RowActions,
  },
  data() {
    return {
      items: [],
      isLoading: false,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      fields: [
        { key: 'srNo', label: '#' },
        { key: 'title', label: 'Title' },
        { key: 'link', label: 'Page Link' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      rejectModalOpen: false,
      selectedPage: null,
      rejectReason: '',
    }
  },
  mounted() {
    this.getCms()
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page
      this.getCms()
    },
    onPerPageChange() {
      this.currentPage = 1
      this.getCms()
    },
    statusLabel(item) {
      if (item.approval_status === 'rejected') return 'Rejected'
      if (item.approval_status === 'approved') return 'Approved'
      return 'Pending'
    },
    statusVariant(item) {
      if (item.approval_status === 'approved') return 'light-success'
      if (item.approval_status === 'rejected') return 'light-danger'
      return 'light-warning'
    },
    formatPageLink(item) {
      const raw = String(item?.link || item?.slug || item?.key || '').trim()
      if (!raw) return '—'
      if (raw.startsWith('http://') || raw.startsWith('https://')) return raw
      return raw.startsWith('/') ? raw : `/${raw}`
    },
    async getCms() {
      this.isLoading = true
      try {
        const res = await adminPanelApi.pages({
          page: this.currentPage,
          per_page: this.perPage,
        })
        const { items } = unwrapPaginatedList(res, ['pages'])
        const payload = unwrapAdminResponse(res)
        applyListPagination(this, res, payload)
        this.items = items
      } catch {
        notifyError(this, 'Unable to fetch CMS pages')
      } finally {
        this.isLoading = false
      }
    },
    removeRow(id) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.value) return
        try {
          await adminPanelApi.deletePage(id)
          notifySuccess(this, 'CMS page deleted successfully.', 'Deleted')
          this.getCms()
        } catch {
          notifyError(this, 'Unable to delete CMS page')
        }
      })
    },
    approvePage(item) {
      this.updateCmsStatus(item, 'approved', '')
    },
    openRejectModal(item) {
      this.selectedPage = item
      this.rejectReason = ''
      this.rejectModalOpen = true
    },
    closeRejectModal() {
      this.rejectModalOpen = false
      this.selectedPage = null
      this.rejectReason = ''
    },
    rejectPage() {
      if (!this.rejectReason.trim()) {
        notifyError(this, 'Please provide a rejection reason', 'Reason required')
        return
      }
      this.updateCmsStatus(this.selectedPage, 'rejected', this.rejectReason.trim())
      this.closeRejectModal()
    },
    async updateCmsStatus(item, approvalStatus, rejectReason) {
      if (!item?.id) return
      try {
        const detailRes = await adminPanelApi.page(item.id)
        const page = unwrapAdminResponse(detailRes)
        await adminPanelApi.savePage({
          id: page.id,
          title: page.title,
          link: page.link,
          content: page.content,
          meta_title: page.meta_title || page.title,
          meta_description: page.meta_description || '',
          keywords: page.keywords || '',
          approval_status: approvalStatus,
          reject_reason: rejectReason || null,
        })
        notifySuccess(this, `CMS page ${approvalStatus} successfully.`, 'Updated')
        this.getCms()
      } catch {
        notifyError(this, 'Unable to update CMS status')
      }
    },
  },
}
</script>

<style scoped>
.cms-page-link {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
  color: #0b1f39;
  word-break: break-all;
}
</style>
